/**
 * THE PACKAGE TERMS, AS THE CUSTOMER READS THEM.
 *
 * Reported by the user, 2026-10-14: *"I can't see the T&C being shown to the customers/guests when purchasing
 * packages, this is really important if not we can't enforce any of these on the customer/guest."* Every other
 * rule in this feature area — the ladder, the two options, expiry, the shop's right to cancel — depends on the
 * customer having been shown these, so they are shown at the point of purchase and accepted explicitly.
 *
 * ── WHY THE TEXT IS HERE AND NOT FETCHED ────────────────────────────────────────────────────────
 *
 * The terms must be READABLE, not assembled from a request: a customer who cannot see them cannot agree to them,
 * and a purchase page that fails to load its own terms must not be able to sell. So they are content in the
 * bundle, rendered by `/packages/terms`, and the acceptance sends the VERSION to the server.
 *
 * ── THE TWO COPIES, AND HOW THEY ARE KEPT HONEST ────────────────────────────────────────────────
 *
 * `docs/PACKAGES_TERMS_AND_CONDITIONS_DRAFT.md` is the legal document (the source of the wording, for review).
 * This is what the customer actually sees. Two copies of legal text WILL drift, so:
 *   • `PACKAGE_TERMS_VERSION` here must equal `utils/packageTerms.js` on the server — the server refuses a
 *     purchase whose page sent a different version, so a stale page cannot accept terms the customer never saw;
 *   • `tests/packageTerms.spec.js` pins the CLAUSES that must appear in both, so a rule cannot be agreed to in
 *     one and missing from the other.
 *
 * BUMP THE VERSION WHENEVER THIS TEXT CHANGES.
 */
export const PACKAGE_TERMS_VERSION = "2026-10-14";

export const PACKAGE_TERMS_PATH = "/packages/terms";

/**
 * The terms, as sections. `table` renders as rows of [what happened, what it costs].
 *
 * Kept as data rather than prose so the page can render headings and tables consistently on a phone — which is
 * where most of these are read.
 */
export const PACKAGE_TERMS_SECTIONS = [
  {
    heading: "1. What you are buying",
    paragraphs: [
      "You are buying a set number of sessions with the services listed, at a discount to the normal price. A session is used when you book it, and you can book each one with any professional who offers that service at the shop.",
      "Prices are those shown when you bought. A later change to the shop's prices, or to the services included, does not change what you already paid for.",
    ],
  },
  {
    heading: "2. When your package starts and ends",
    paragraphs: [
      "Your package starts when your payment is confirmed and ends on the expiry date shown on your package and in the confirmation email.",
      "If you cannot get a slot, the clock pauses for the period you could not book — you are not charged for time the shop could not serve.",
    ],
  },
  {
    heading: "3. Booking your sessions",
    paragraphs: [
      "You book a session exactly as you would book and pay for any appointment. The difference is that your package pays for it instead of your card, and no money changes hands at that moment.",
      "A session must be booked before your package expires.",
    ],
  },
  {
    heading: "4. If you cancel, or do not turn up",
    table: [
      [
        `You cancel with at least the shop's notice period`,
        "Nothing — the session goes back on your package",
      ],
      [
        "You cancel inside that notice period",
        "The session is LOST. You choose what happens to it — see 4.1",
      ],
      ["You do not attend and do not cancel", "The session is lost, and nothing is returned"],
      ["The shop or the professional cancels", "Nothing — your session is returned, whatever the notice"],
    ],
    paragraphs: [
      "The notice period is the shop's own policy. It is shown to you before you book, and again before you confirm a cancellation.",
    ],
  },
  {
    heading: "4.1 Cancelling inside the notice period — your two options",
    paragraphs: [
      "The sessions you had booked for that appointment are LOST. They are not returned automatically. Before you confirm, you are shown both options below, with the figures for your own booking.",
    ],
    table: [
      [
        "Keep the sessions (the default)",
        "The sessions are held for you. You pay the shop's late-cancellation charge — a percentage of what those sessions were worth at the price YOU paid — and they go straight back on your package. Pay now, or whenever you next want to book with it.",
      ],
      [
        "Take the refund (only if a refund is due)",
        "The sessions stay lost and the refundable part of their value is returned to the card you paid with. This option is offered only when a refund is actually due.",
      ],
    ],
    paragraphs: [
      "Both figures are worked out from the discounted price you actually paid, not from the shop's usual price for those services. What you keep and what you are charged for always add up to no more than the price of your package, so a cancellation late in the day can never return more money than you paid.",
      "These are alternatives, not both. You cannot take the refund and later also keep the sessions, because that would return more than the sessions were ever worth.",
      "If you take neither and the package expires, the held sessions end with the package, exactly as an unused session does.",
      "Everything else on your package keeps working normally while sessions are held: you are never prevented from using the sessions you still have.",
    ],
  },
  {
    heading: "5. If we cancel",
    paragraphs: [
      "If the shop or the professional cancels, you never lose a session, and no cancellation charge of any kind applies — whatever the notice — because the cancellation is not yours.",
    ],
  },
  {
    heading: "6. Refunds",
    table: [
      ["Before you use any session", "A full refund"],
      [
        "After you have used one or more sessions",
        "A refund of the unused sessions, worked out on the price you actually paid — not the full service price",
      ],
      [
        "Sessions lost by a cancellation you chose to refund",
        "The refundable part of those sessions' value, as shown to you when you cancelled — worked out on the price you paid, so the refund plus any sessions you keep can never exceed what you paid",
      ],
      ["Any time", "Less any payment processing cost that cannot be recovered from our payment provider"],
    ],
    paragraphs: [
      "A package is refunded in parts. A refund following one cancellation returns that cancellation's share only; your package stays live for everything else, and the total refunded across its life can never be more than you paid for it.",
      "Refunds go back to your original payment method within the period stated on your receipt.",
    ],
  },
  {
    heading: "7. If your package cannot be delivered",
    paragraphs: [
      "If no professional who offers your services can deliver the remaining sessions, you are entitled to a full refund of the undelivered part. This is your right, not a favour we choose to grant.",
    ],
  },
  {
    heading: "8. Expiry",
    paragraphs: [
      "Unused sessions expire on the expiry date. They do not become credit and they are not carried over — with one exception: if you could not get a slot, the clock pauses for that period (see section 2).",
      "Sessions you held after cancelling inside the notice period also end with the package on its expiry date.",
      "We remind you before your package expires by email and SMS.",
    ],
  },
  {
    heading: "9. Who the package belongs to",
    paragraphs: [
      "Your package is yours. It cannot be sold or given to somebody else, and it is used with the email and phone number you gave when you bought it.",
      "If you were given a package by somebody else, the number you gave when you accepted it is the number we identify you by.",
    ],
  },
  {
    heading: "10. If the business stops offering the package",
    paragraphs: [
      "If the business leaves the platform, you are repaid the full price of your unused sessions, with no fees deducted.",
    ],
  },
  {
    heading: "11. Changes to these terms",
    paragraphs: [
      "The terms you accepted are recorded on your purchase, with the date. We may change these terms, and the version you accepted is the version that applies to what you bought.",
      "What you agreed to is what applies to you.",
    ],
  },
];

/** A short plain-language box shown above the full terms, so the important parts are read at all. */
export const PACKAGE_TERMS_SUMMARY = [
  "Your package gives you a set number of sessions at a discount, until the expiry date shown.",
  "Cancel before the shop's notice period and the session goes back on your package. Cancel inside it and you choose: keep the sessions by paying the late-cancellation charge, or (when a refund is due) take the money back.",
  "If the shop cancels, you never lose a session.",
  "Unused sessions expire; they do not become credit. We remind you before that happens.",
  "If nobody can deliver your sessions, you get a full refund. That is your right.",
];
